"use client";

import Image from "next/image";
import {
  ArrowUpDown,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Eye,
  Filter,
  MapPin,
  Phone,
  Search,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

const caseImages = (no: string, count: number) =>
  Array.from(
    { length: count },
    (_, index) => `/cases/${no}/${String(index + 1).padStart(2, "0")}.jpg`,
  );

const cases = [
  {
    no: "01",
    title: "외벽 청소",
    area: "서울 중랑구",
    date: "2026-06-13",
    views: 214,
    category: "외벽청소",
    images: caseImages("01", 7),
    description:
      "벽돌 외벽과 창틀 주변에 누적된 오염을 고압 세척으로 정리했습니다. 물 튐과 하부 동선을 확인하며 구간별로 작업했습니다.",
  },
  {
    no: "02",
    title: "사무실 청소",
    area: "서울 광진구",
    date: "2026-06-13",
    views: 207,
    category: "사무실청소",
    images: caseImages("02", 5),
    description:
      "오염과 잔여물이 남은 바닥을 세척한 뒤 표면 상태를 확인했습니다. 작업 전후 차이가 잘 보이도록 주요 구간을 나누어 관리했습니다.",
  },
  {
    no: "03",
    title: "사무실 청소",
    area: "서울 성북구",
    date: "2026-06-13",
    views: 203,
    category: "사무실청소",
    images: caseImages("03", 8),
    description:
      "사무공간과 공용 출입구의 바닥, 유리, 금속 마감 상태를 함께 점검했습니다. 방문객 동선이 많은 공간이라 마감 균일도와 안전을 우선했습니다.",
  },
  {
    no: "04",
    title: "외벽 청소",
    area: "서울 동대문구",
    date: "2026-06-13",
    views: 195,
    category: "외벽청소",
    images: caseImages("04", 6),
    description:
      "외부 난간과 창틀 주변의 먼지, 흙물 자국, 배수 구간 오염을 정리했습니다. 주변 낙하물과 물 흐름을 확인하며 안전하게 진행했습니다.",
  },
  {
    no: "05",
    title: "사무실 청소",
    area: "서울 노원구",
    date: "2026-06-13",
    views: 189,
    category: "사무실청소",
    images: caseImages("05", 8),
    description:
      "상가 내부의 대형 타일 바닥과 유리 주변을 정리했습니다. 입점 전후로 바로 사용할 수 있도록 바닥 얼룩과 잔여 오염을 집중 관리했습니다.",
  },
  {
    no: "06",
    title: "외벽 청소",
    area: "서울 강북구",
    date: "2026-06-13",
    views: 198,
    category: "외벽청소",
    images: caseImages("06", 4),
    description:
      "건물 외부 유리면의 물때와 먼지를 장대 장비로 세척했습니다. 높은 위치의 유리도 구역을 나누어 물자국을 줄이도록 마감했습니다.",
  },
  {
    no: "07",
    title: "외벽 청소",
    area: "서울 중랑구",
    date: "2020-07-14",
    views: 198,
    category: "외벽청소",
    images: ["/cases/07/01.jpg"],
    description:
      "외벽 오염 상태와 주변 보행 동선을 확인한 뒤 안전 구역을 확보하고 청소를 진행했습니다. 마감 후 주요 면의 잔여 오염을 재점검했습니다.",
  },
];

const contactNumber = "010-6261-0085";

const serviceNotes = [
  "공용부, 바닥, 유리, 외벽 등 작업 범위 확인",
  "현장 사진 기반 사전 견적 안내",
  "작업 완료 후 주요 구역 확인",
  "업무시간과 운영 동선을 고려한 방문 조율",
];

type SortType = "latest" | "views";

export default function Home() {
  const [activeTab, setActiveTab] = useState("전체");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortType>("latest");
  const [selectedNo, setSelectedNo] = useState("01");
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);

  const filteredCases = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return cases
      .filter((item) => activeTab === "전체" || item.category === activeTab)
      .filter((item) => {
        if (!normalizedQuery) return true;
        return `${item.title} ${item.area} ${item.date}`
          .toLowerCase()
          .includes(normalizedQuery);
      })
      .sort((a, b) => {
        if (sort === "views") return b.views - a.views;
        return b.date.localeCompare(a.date);
      });
  }, [activeTab, query, sort]);

  const selectedCase =
    cases.find((item) => item.no === selectedNo) ?? filteredCases[0] ?? cases[0];
  const selectedImage =
    selectedCase.images[selectedImageIndex] ?? selectedCase.images[0];
  const hasMultipleImages = selectedCase.images.length > 1;

  useEffect(() => {
    if (!isImageDialogOpen) return;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsImageDialogOpen(false);
      }
    };

    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [isImageDialogOpen]);

  const selectCase = (no: string) => {
    setSelectedNo(no);
    setSelectedImageIndex(0);
    setIsImageDialogOpen(false);
  };

  const moveImage = (direction: -1 | 1) => {
    setSelectedImageIndex((currentIndex) => {
      const imageCount = selectedCase.images.length;
      return (currentIndex + direction + imageCount) % imageCount;
    });
  };

  const copyContactNumber = async () => {

    try {
      await navigator.clipboard.writeText(contactNumber);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = contactNumber;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.top = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      document.body.removeChild(textarea);

    }
  };

  return (
    <main className="app-shell">
      <header className="topbar">
        <a className="brand" href="#workspace" aria-label="삼성크린환경 홈">
          <span className="brand-symbol" aria-hidden="true">
            <span />
          </span>
          <span className="brand-text">
            <strong>삼성크린환경</strong>
            <small>청소전문기업</small>
          </span>
        </a>
        <nav className="top-nav" aria-label="주요 메뉴">
          <a href="#workspace">작업현황</a>
          <a href="#cases">시공사례</a>
          <a href="#service">청소안내</a>
          <a href="#contact">연락처</a>
        </nav>
      </header>

      <section className="workspace" id="workspace">
        <div className="workspace-head">
          <div>
            <p className="section-kicker">Samsung Clean</p>
            <h1>시공 상담 보드</h1>
            <p>
              삼성크린환경의 주요 청소 유형과 기존 시공사례를 한 화면에서
              확인할 수 있습니다.
            </p>
          </div>
          <div className="head-actions">
            <a className="ghost-action" href="#cases">
              <Filter size={16} />
              사례 보기
            </a>
            <button
              className="primary-action"
              type="button"
              onClick={copyContactNumber}
            >
              <Phone size={16} />
              010-6261-0085
            </button>
          </div>
        </div>

        <div className="work-grid">
          <section className="list-panel" id="cases" aria-labelledby="case-title">
            <div className="panel-head">
              <div>
                <h2 id="case-title">시공사례</h2>
                <p>{filteredCases.length}건의 작업 이력이 표시됩니다.</p>
              </div>
              <div className="sort-menu" aria-label="정렬 방식">
                <ArrowUpDown size={15} />
                <button
                  type="button"
                  data-active={sort === "latest"}
                  onClick={() => setSort("latest")}
                >
                  최신순
                </button>
                <button
                  type="button"
                  data-active={sort === "views"}
                  onClick={() => setSort("views")}
                >
                  조회순
                </button>
              </div>
            </div>

            <div className="toolbar">
              <label className="search-box">
                <Search size={17} />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="지역, 유형, 날짜 검색"
                />
              </label>
            </div>

            <div className="tabs" aria-label="시공사례 분류">
              {["전체", "사무실청소", "줄광청소", "외벽청소"].map(
                (tab) => (
                  <button
                    key={tab}
                    type="button"
                    data-active={activeTab === tab}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab}
                  </button>
                ),
              )}
            </div>

            <div className="case-list-wrap">
              {filteredCases.length > 0 ? (
                <ul className="case-list">
                  {filteredCases.map((item) => (
                    <li key={item.no}>
                      <button
                        className="case-row"
                        data-active={selectedCase.no === item.no}
                        type="button"
                        onClick={() => selectCase(item.no)}
                      >
                        <Image
                          src={item.images[0]}
                          alt={`${item.title} ${item.no}`}
                          width={132}
                          height={96}
                        />
                        <span className="case-content">
                          <span className="case-title-line">
                            <strong>{item.title}</strong>
                          </span>
                          <span className="case-meta">
                            <MapPin size={14} />
                            {item.area}
                          </span>
                          <span className="case-sub">
                            <Clock3 size={14} />
                            {item.date}
                            <Eye size={14} />
                            조회 {item.views}
                          </span>
                        </span>
                        <ChevronDown className="row-icon" size={16} />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="empty-state">
                  <Search size={22} />
                  <strong>검색 결과가 없습니다</strong>
                  <p>검색어를 줄이거나 다른 청소 유형을 선택해보세요.</p>
                </div>
              )}
            </div>
          </section>

          <aside className="detail-panel" aria-label="선택한 시공사례 상세">
            <div className="preview-image">
              <button
                className="preview-image-button"
                type="button"
                aria-label={`${selectedCase.title} 사진 크게 보기`}
                onClick={() => setIsImageDialogOpen(true)}
              >
                <Image
                  src={selectedImage}
                  alt={`${selectedCase.title} 상세 이미지`}
                  width={720}
                  height={520}
                  priority
                />
                <span className="preview-image-hint">사진을 클릭해 크게 보기</span>
              </button>
              {hasMultipleImages ? (
                <>
                  <button
                    className="image-nav image-nav-left"
                    type="button"
                    aria-label="이전 사진"
                    onClick={() => moveImage(-1)}
                  >
                    <ChevronLeft size={22} />
                  </button>
                  <button
                    className="image-nav image-nav-right"
                    type="button"
                    aria-label="다음 사진"
                    onClick={() => moveImage(1)}
                  >
                    <ChevronRight size={22} />
                  </button>
                </>
              ) : null}
              <span className="image-count">
                {selectedImageIndex + 1} / {selectedCase.images.length}
              </span>
            </div>
            <div className="detail-body">
              <span className="badge">{selectedCase.category}</span>
              <h2>{selectedCase.title}</h2>
              <div className="detail-meta">
                <span>
                  <MapPin size={14} />
                  {selectedCase.area}
                </span>
                <span>
                  <Eye size={14} />
                  조회 {selectedCase.views}
                </span>
              </div>
              <p>{selectedCase.description}</p>
              <div className="check-list">
                {serviceNotes.map((note) => (
                  <span key={note}>
                    <Check size={14} />
                    {note}
                  </span>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>

      {isImageDialogOpen ? (
        <div
          className="image-dialog-backdrop"
          role="presentation"
          onClick={() => setIsImageDialogOpen(false)}
        >
          <div
            className="image-dialog"
            role="dialog"
            aria-modal="true"
            aria-label={`${selectedCase.title} 확대 사진`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="image-dialog-head">
              <div>
                <strong>{selectedCase.title}</strong>
                <span>
                  {selectedImageIndex + 1} / {selectedCase.images.length}
                </span>
              </div>
              <button
                className="dialog-close"
                type="button"
                aria-label="확대 사진 닫기"
                onClick={() => setIsImageDialogOpen(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="image-dialog-stage">
              <button
                className="dialog-image-button"
                type="button"
                aria-label="확대 사진 닫기"
                onClick={() => setIsImageDialogOpen(false)}
              >
                <Image
                  src={selectedImage}
                  alt={`${selectedCase.title} 확대 이미지`}
                  width={1600}
                  height={1200}
                  priority
                />
              </button>
              {hasMultipleImages ? (
                <>
                  <button
                    className="dialog-image-nav dialog-image-nav-left"
                    type="button"
                    aria-label="이전 확대 사진"
                    onClick={() => moveImage(-1)}
                  >
                    <ChevronLeft size={24} />
                  </button>
                  <button
                    className="dialog-image-nav dialog-image-nav-right"
                    type="button"
                    aria-label="다음 확대 사진"
                    onClick={() => moveImage(1)}
                  >
                    <ChevronRight size={24} />
                  </button>
                </>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      <section className="info-section" id="service">
        <div className="section-title">
          <p className="section-kicker">Cleaning Service</p>
          <h2>현장별 청소 안내</h2>
        </div>
        <div className="service-table">
          {[
            ["사무실청소", "회의실, 사무공간, 공용부 위생 관리"],
            ["특수크리닝", "학교, 학원, 어린이집 등 위생 기준이 중요한 공간"],
            ["줄광청소", "바닥 표면 세척과 광택 관리 작업"],
            ["외벽청소", "외벽, 유리, 창틀 등 외부 오염 정리"],
          ].map(([title, desc]) => (
            <div className="service-line" key={title}>
              <strong>{title}</strong>
              <p>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="company-strip" id="contact">
        <div>
          <p className="section-kicker">Contact</p>
          <h2>상담 전 현장 종류와 희망 작업일을 알려주세요</h2>
        </div>
        <div className="contact-box">
          <button type="button" onClick={copyContactNumber}>
            <Phone size={16} />
            010-6261-0085
          </button>
        </div>
      </section>

      <footer className="footer">
        <strong>삼성크린환경</strong>
        <span>대표 김희석 · 사업자등록번호 204-11-23315 · 서울특별시 중랑구 용마산로 94길 79</span>
      </footer>
    </main>
  );
}
